/**
 * Members Redux Slice
 * Manages members state
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del } from '../../services/api/axiosInstance';
import { API_ENDPOINTS } from '../../services/api/apiEndpoints';

// Async thunks
export const fetchMembers = createAsyncThunk(
  'members/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.MEMBERS.BASE, params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch members'
      );
    }
  }
);

export const fetchMemberById = createAsyncThunk(
  'members/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.MEMBERS.BY_ID(id));
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch member'
      );
    }
  }
);

export const createMember = createAsyncThunk(
  'members/create',
  async (memberData, { rejectWithValue }) => {
    try {
      const response = await post(API_ENDPOINTS.MEMBERS.BASE, memberData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create member'
      );
    }
  }
);

export const updateMember = createAsyncThunk(
  'members/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await put(API_ENDPOINTS.MEMBERS.BY_ID(id), data);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update member'
      );
    }
  }
);

export const deleteMember = createAsyncThunk(
  'members/delete',
  async (id, { rejectWithValue }) => {
    try {
      await del(API_ENDPOINTS.MEMBERS.BY_ID(id));
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete member'
      );
    }
  }
);

// Initial state
const initialState = {
  members: [],
  selectedMember: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  isLoading: false,
  error: null,
};

// Slice
const memberSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedMember: (state) => {
      state.selectedMember = null;
    },
    setSelectedMember: (state, action) => {
      state.selectedMember = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch all members
    builder
      .addCase(fetchMembers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMembers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.members = action.payload.data || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch member by ID
    builder
      .addCase(fetchMemberById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMemberById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedMember = action.payload;
      })
      .addCase(fetchMemberById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Create member
    builder
      .addCase(createMember.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.members.unshift(action.payload);
      })
      .addCase(createMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Update member
    builder
      .addCase(updateMember.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateMember.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.members.findIndex(
          (m) => m.id === action.payload.id
        );
        if (index !== -1) {
          state.members[index] = action.payload;
        }
        state.selectedMember = action.payload;
      })
      .addCase(updateMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Delete member
    builder
      .addCase(deleteMember.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.members = state.members.filter((m) => m.id !== action.payload);
      })
      .addCase(deleteMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSelectedMember, setSelectedMember } =
  memberSlice.actions;
export default memberSlice.reducer;
